import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3, CreditCard, Settings, TrendingUp,
  Menu, X, RefreshCw, LogOut, DollarSign, Building2, Settings2, BookOpen,
  ChevronDown, ChevronRight, Wallet, Package, Users
} from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.FC<any>;
  children?: NavItem[];
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const location = useLocation();
  const { user, signOut } = useAuth();

  const navigation: NavItem[] = [
    { name: 'Dashboard', href: '/', icon: BarChart3 },
    { name: 'Transactions', href: '/payments', icon: CreditCard },
    { name: 'Analytics', href: '/analytics', icon: TrendingUp },
    { name: 'Financials', href: '/financials', icon: BookOpen, children: [
      { name: 'Overview', href: '/financials', icon: BookOpen },
      { name: 'Credit Cards', href: '/financials/credit-cards', icon: Wallet },
      { name: 'Club & Trade-In', href: '/financials/keap-orders', icon: Package },
      { name: 'Callaway', href: '/financials/callaway', icon: Package },
      { name: 'True Cash', href: '/financials/true-cash', icon: Wallet },
      { name: 'Business Lines', href: '/financials/business-lines', icon: BookOpen },
      { name: 'Membership', href: '/financials/membership', icon: Users },
      { name: 'Post-School Sales', href: '/financials/post-school', icon: Package },
      { name: 'SP30 Cohorts', href: '/financials/sp30', icon: Users },
      { name: 'New Customers', href: '/financials/new-customers', icon: Users },
      { name: 'First Purchase LTV', href: '/financials/first-product-ltv', icon: TrendingUp },
      { name: 'Club Buyers', href: '/financials/club-buyers', icon: Package },
    ]},
    { name: 'Customers', href: '/customers', icon: Users },
    { name: 'Categorization', href: '/categorization', icon: Settings2 },
    { name: 'Platforms', href: '/platforms', icon: Building2 },
    { name: 'Shopify Payouts', href: '/shopify-payouts', icon: DollarSign },
    { name: 'Historical Sync', href: '/historical-sync', icon: RefreshCw },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  // Auto-expand Financials section when on any /financials/* route
  React.useEffect(() => {
    if (location.pathname.startsWith('/financials')) {
      setExpandedSections(prev => new Set(prev).add('Financials'));
    }
  }, [location.pathname]);

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname === href;
  };

  const isSectionActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const toggleSection = (name: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
      setUserMenuOpen(false);
    } catch (error) {
      toast.error('Error signing out');
    }
  };

  const getUserDisplayName = () => {
    if (user?.user_metadata?.name) return user.user_metadata.name;
    return user?.email || 'User';
  };

  const getUserInitials = () => {
    const name = getUserDisplayName();
    if (name === 'User') return 'U';
    return name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const SidebarContent = () => (
    <>
      <div className="flex items-center h-16 px-4 border-b border-gray-100">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <BarChart3 className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-sm font-bold text-gray-900 leading-tight">Graves Golf</h1>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Admin Dashboard</p>
          </div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navigation.map((item) => {
          const Icon = item.icon;

          if (item.children) {
            const sectionActive = isSectionActive(item.href);
            const expanded = expandedSections.has(item.name);
            return (
              <div key={item.name}>
                <button
                  onClick={() => toggleSection(item.name)}
                  className={`w-full group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    sectionActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className={`mr-3 h-4 w-4 ${sectionActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'}`} />
                  <span className="flex-1 text-left">{item.name}</span>
                  {expanded
                    ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                    : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                  }
                </button>
                {expanded && (
                  <div className="ml-4 mt-1 space-y-1">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      const childActive = isActive(child.href);
                      return (
                        <Link
                          key={child.href}
                          to={child.href}
                          onClick={() => setSidebarOpen(false)}
                          className={`group flex items-center px-3 py-1.5 text-sm rounded-lg transition-colors ${
                            childActive
                              ? 'text-blue-700 font-medium bg-blue-50/50'
                              : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                          }`}
                        >
                          <ChildIcon className={`mr-3 h-3.5 w-3.5 ${childActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'}`} />
                          {child.name}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          const active = isActive(item.href) || isSectionActive(item.href);
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                active
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Icon className={`mr-3 h-4 w-4 ${active ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'}`} />
              {item.name}
            </Link>
          );
        })}
      </nav>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-900/50" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white shadow-xl">
          <div className="absolute top-4 right-4">
            <button onClick={() => setSidebarOpen(false)} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>
          <SidebarContent />
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-60 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <SidebarContent />
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-60">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-14 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2 p-2 text-gray-500 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />

            {/* User menu */}
            <div className="relative flex items-center">
              <button
                type="button"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-2 rounded-full bg-gray-50 px-3 py-1.5 text-sm hover:bg-gray-100 transition-colors"
              >
                <div className="h-7 w-7 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium">
                  {getUserInitials()}
                </div>
                <span className="hidden md:block text-gray-700 font-medium text-sm">
                  {getUserDisplayName()}
                </span>
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-48 rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5 z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{getUserDisplayName()}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <LogOut className="mr-3 h-4 w-4 text-gray-400" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
